import { useState, useEffect, useCallback, useRef } from 'react';
import {
  FiEdit,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiStar,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import {
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  addToast,
} from '@heroui/react';
import ReviewForm from '../../../../feature-module/components/ReviewForm/ReviewForm';
import { createReview, updateReview } from '../../../../service/reviewService';
import {
  Review,
  CreateReviewData,
  UpdateReviewData,
} from '../../../../utils/type';
import ReviewImages from '../../../components/ReviewImages/ReviewImages';
import StarRating from '../../../components/StarRating';
import { getUserDisplayName } from '../../../../utils/userUtils';
import { validateReviewForProfanity } from '../../../../utils/profanityFilter';
import { useAuth } from '../../../../context/AuthContext';
import PageHeader from '../../../components/common/ui/PageHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFetchReviewByUserId } from '../../../../hook/useQueryData';
import { getBookingById } from '../../../../service/bookingService';
import LoadingSpinner from '../../../components/common/loading/LoadingSprinner';
import CustomPagination from '../../../components/common/ui/CustomPagination';

// ── helpers ────────────────────────────────────────────────────────

const slugify = (s?: string) =>
  (s || 'service')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

const validRating = (v: any): number | null =>
  typeof v === 'number' && v >= 1 && v <= 5 ? Math.round(v) : null;

/** Fill all rating fields, falling back to overall rating (min 1). */
const fillRatingFields = (review: Partial<Review>) => {
  const fallback = Math.max(Math.round(review.rating || 0), 1);
  return {
    serviceRating: validRating(review.serviceRating) ?? fallback,
    qualityRating: validRating(review.qualityRating) ?? fallback,
    valueRating: validRating(review.valueRating) ?? fallback,
    communicationRating: validRating(review.communicationRating) ?? fallback,
    timelinessRating: validRating(review.timelinessRating) ?? fallback,
  };
};

const isReviewEditable = (reviewDate?: string | Date): boolean => {
  if (!reviewDate) return false;
  return Date.now() - new Date(reviewDate).getTime() <= 2 * 24 * 60 * 60 * 1000;
};

const getTimeRemaining = (reviewDate?: string | Date): string => {
  if (!reviewDate) return '';
  const diff =
    2 * 24 * 60 * 60 * 1000 - (Date.now() - new Date(reviewDate).getTime());
  if (diff <= 0) return 'EXPIRED';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

const isContentCleaned = (text: string) =>
  !!(text && (text.includes('*') || text.includes('***')));

const RATING_FILTERS = [
  { key: 'all', label: 'All Reviews' },
  { key: '5', label: '5★' },
  { key: '4', label: '4★' },
  { key: '3', label: '3★' },
  { key: '2', label: '2★' },
  { key: '1', label: '1★' },
];

// ── component ──────────────────────────────────────────────────────

const Reviews = () => {
  const { user, userId, isVerifyUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [reviews, setReviews] = useState<Review[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterRating, setFilterRating] = useState('all');
  const reviewsPerPage = 10;

  // edit form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // view modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedViewReview, setSelectedViewReview] = useState<Review | null>(
    null
  );

  // image viewer
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [viewerImages, setViewerImages] = useState<string[]>([]);
  const [imageLoading, setImageLoading] = useState(false);

  // profanity warning
  const [showProfanityWarning, setShowProfanityWarning] = useState(false);
  const [profanityWarningData, setProfanityWarningData] = useState<{
    originalData: Review;
    cleanedData: any;
    errors: string[];
  } | null>(null);

  // URL booking review
  const [urlBookingId, setUrlBookingId] = useState<string | null>(null);
  const [urlBookingData, setUrlBookingData] = useState<{
    providerId: string;
    serviceId: string;
    serviceName: string;
    bookingId: string;
  } | null>(null);
  const [showUrlReviewForm, setShowUrlReviewForm] = useState(false);

  // refs for keyboard nav (avoid stale closures in [] effects)
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const totalPagesRef = useRef(0);
  const showImageViewerRef = useRef(showImageViewer);
  showImageViewerRef.current = showImageViewer;
  const viewerImagesRef = useRef(viewerImages);
  viewerImagesRef.current = viewerImages;

  // tick for countdown timer display
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // ── data fetching ────────────────────────────────────────────────

  const {
    data: response,
    isLoading: isLoadingReviews,
    error: reviewsError,
    refetch,
  } = useFetchReviewByUserId(userId || '', {
    page: currentPage,
    limit: reviewsPerPage,
    rating: filterRating === 'all' ? undefined : Number(filterRating),
  });

  const totalPages = response?.data?.pagination?.totalPages || 0;
  totalPagesRef.current = totalPages;

  useEffect(() => {
    if (response?.data?.review) setReviews(response.data.review);
  }, [response]);

  // URL booking → open review form
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const bid = params.get('bookingId');
    setUrlBookingId(bid);
  }, [location.search]);

  useEffect(() => {
    if (!urlBookingId) return;
    getBookingById(urlBookingId)
      .then((booking: any) => {
        if (booking?.booking) booking = booking.booking;
        if (!booking) return;
        setUrlBookingData({
          providerId: booking.providerId || 'default-provider',
          serviceId: booking.serviceId || 'default-service',
          serviceName:
            booking.serviceName ||
            booking.serviceDetails?.serviceTitle ||
            'Service Review',
          bookingId: booking.bookingId || urlBookingId,
        });
        setShowUrlReviewForm(true);
      })
      .catch(() => {});
  }, [urlBookingId]);

  // ── image viewer ─────────────────────────────────────────────────

  const openImageViewer = (images: string[], startIndex = 0) => {
    setViewerImages(images);
    setCurrentImageIndex(startIndex);
    setImageLoading(true);
    setShowImageViewer(true);
  };

  const closeImageViewer = () => {
    setShowImageViewer(false);
    setCurrentImageIndex(0);
    setViewerImages([]);
    setImageLoading(false);
  };

  const goToPreviousImage = () => {
    setImageLoading(true);
    setCurrentImageIndex((p) =>
      p > 0 ? p - 1 : viewerImagesRef.current.length - 1
    );
  };

  const goToNextImage = () => {
    setImageLoading(true);
    setCurrentImageIndex((p) =>
      p < viewerImagesRef.current.length - 1 ? p + 1 : 0
    );
  };

  // ── keyboard navigation ──────────────────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      )
        return;

      if (showImageViewerRef.current) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goToPreviousImage();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goToNextImage();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          closeImageViewer();
        }
        return;
      }

      if (e.key === 'ArrowLeft' && currentPageRef.current > 1) {
        e.preventDefault();
        setCurrentPage((p) => p - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (
        e.key === 'ArrowRight' &&
        currentPageRef.current < totalPagesRef.current
      ) {
        e.preventDefault();
        setCurrentPage((p) => p + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (e.key === 'Home' && totalPagesRef.current > 1) {
        e.preventDefault();
        setCurrentPage(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (e.key === 'End' && totalPagesRef.current > 1) {
        e.preventDefault();
        setCurrentPage(totalPagesRef.current);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!showImageViewer) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeImageViewer();
      }
    };
    document.addEventListener('keydown', onEsc, true);
    return () => document.removeEventListener('keydown', onEsc, true);
  }, [showImageViewer]);

  // ── pagination ───────────────────────────────────────────────────

  const handlePageChange = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    [totalPages]
  );

  const handleFilterChange = (rating: string) => {
    setFilterRating(rating);
    setCurrentPage(1);
  };

  // ── calculate overall rating ─────────────────────────────────────

  const overallRating = useCallback(
    (review: Partial<Review> | null | undefined): number => {
      if (!review) return 0;

      // Prefer the precise decimal average from the backend.
      if (
        typeof (review as any).overallRating === 'number' &&
        (review as any).overallRating > 0
      ) {
        return Math.min((review as any).overallRating, 5);
      }

      // Fall back to the integer rating field.
      if (typeof review.rating === 'number' && review.rating > 0) {
        return Math.min(review.rating, 5);
      }

      // Last resort: derive from sub-ratings.
      const ratings = [
        review.serviceRating,
        review.qualityRating,
        review.valueRating,
        review.communicationRating,
        review.timelinessRating,
      ].filter((v): v is number => typeof v === 'number' && v > 0);
      if (!ratings.length) return 0;

      return Math.min(ratings.reduce((a, b) => a + b, 0) / ratings.length, 5);
    },
    []
  );

  // ── view modal ───────────────────────────────────────────────────

  const handleViewReview = (review: Review) => {
    setSelectedViewReview({
      ...review,
      rating: overallRating(review),
    } as Review);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setShowViewModal(false);
    setSelectedViewReview(null);
  };

  // ── review submit ────────────────────────────────────────────────

  const submitReview = async (
    reviewData: Review,
    skipProfanityCheck = false
  ) => {
    try {
      // profanity check
      if (!skipProfanityCheck) {
        const validation = validateReviewForProfanity({
          title: reviewData.title,
          comment: reviewData.comment,
        });
        if (!validation.isValid) {
          setProfanityWarningData({
            originalData: reviewData,
            cleanedData: validation.cleanedData,
            errors: validation.errors,
          });
          setShowProfanityWarning(true);
          addToast({
            title: 'Content Review Required',
            description:
              'Inappropriate language detected. Please review the suggested changes.',
            color: 'warning',
          });
          return;
        }
      }

      // validate
      if (!reviewData.serviceId?.trim()) {
        addToast({
          title: 'Error',
          description: 'Service ID is required.',
          color: 'danger',
        });
        return;
      }
      if (
        !reviewData.rating ||
        reviewData.rating < 1 ||
        reviewData.rating > 5
      ) {
        addToast({
          title: 'Error',
          description: 'Please provide a valid rating between 1 and 5.',
          color: 'danger',
        });
        return;
      }
      if (!reviewData.title?.trim()) {
        addToast({
          title: 'Error',
          description: 'Please provide a review title.',
          color: 'danger',
        });
        return;
      }
      if (!reviewData.comment?.trim()) {
        addToast({
          title: 'Error',
          description: 'Please write your review.',
          color: 'danger',
        });
        return;
      }
      const commentLen = reviewData.comment.trim().length;
      if (commentLen < 10 || commentLen > 2000) {
        addToast({
          title: 'Error',
          description: 'Review must be between 10 and 2000 characters.',
          color: 'danger',
        });
        return;
      }

      const ratingFields = fillRatingFields(reviewData);

      if (isEditMode && selectedReview) {
        // UPDATE
        const reviewId =
          (selectedReview as any).id ||
          (selectedReview as any)._id ||
          (selectedReview as any).reviewId;
        if (!reviewId) throw new Error('Invalid review ID');

        const updateData: UpdateReviewData = {
          title: reviewData.title,
          comment: reviewData.comment,
          rating: Math.round(reviewData.rating),
          imageUrls: reviewData.imageUrls,
          imageNames: reviewData.imageNames || [],
          ...ratingFields,
        };

        await updateReview(reviewId, updateData);
        addToast({
          title: 'Review Updated',
          description: `Your review has been updated successfully.`,
          color: 'success',
        });
      } else {
        // CREATE
        const createData: CreateReviewData = {
          providerId: reviewData.providerId || 'default-provider',
          serviceId: reviewData.serviceId.trim(),
          title: reviewData.title.trim(),
          comment: reviewData.comment.trim(),
          rating: Math.round(reviewData.rating),
          imageNames: reviewData.imageNames || [],
          userName: getUserDisplayName(user?.name || 'User'),
          userEmail: user?.email || '',
          userId: userId || (user as any)?._id,
          userProfileImage: user?.profilePicture || '',
          isVerified: isVerifyUser || false,
          date: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          ...ratingFields,
        };

        if (
          reviewData.bookingId &&
          reviewData.bookingId !== 'undefined' &&
          reviewData.bookingId !== 'null'
        ) {
          createData.bookingId = reviewData.bookingId;
        }

        await createReview(createData);
        addToast({
          title: 'Review Submitted',
          description: `Your review has been submitted successfully.`,
          color: 'success',
        });
      }

      refetch();
    } catch (error: any) {
      let msg = 'Failed to submit review. Please try again.';
      if (error.response?.status === 422)
        msg = 'Please check your review data and try again.';
      else if (error.response?.status === 413)
        msg = 'Images too large (max 5MB each).';
      else if (error.response?.data?.message) msg = error.response.data.message;
      addToast({ title: 'Error', description: msg, color: 'danger' });
    } finally {
      setShowReviewForm(false);
      setSelectedReview(null);
      setIsEditMode(false);
    }
  };

  const handleAcceptCleanedReview = async () => {
    if (!profanityWarningData) return;
    const cleaned = {
      ...profanityWarningData.originalData,
      title:
        profanityWarningData.cleanedData?.title ||
        profanityWarningData.originalData.title,
      comment:
        profanityWarningData.cleanedData?.review ||
        profanityWarningData.originalData.comment,
    };
    setShowProfanityWarning(false);
    setProfanityWarningData(null);
    await submitReview(cleaned, true);
  };

  const handleRejectCleanedReview = () => {
    setShowProfanityWarning(false);
    setProfanityWarningData(null);
    addToast({
      title: 'Cancelled',
      description: 'Please edit your review and try again.',
      color: 'default',
    });
  };

  // ── navigation ───────────────────────────────────────────────────

  const goToService = (rev: any) => {
    const id = rev?.serviceId;
    if (!id) return;
    navigate(
      `/services/service-details/${rev?.displayId || id}/${slugify(rev?.serviceName || rev?.title)}`
    );
  };

  // ── loading ──────────────────────────────────────────────────────

  if (isLoadingReviews && !reviews.length) {
    return (
      <div>
        <PageHeader
          title="Reviews"
          description="Manage your reviews and ratings."
        />
        <div className="flex justify-center py-20">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // ── render ───────────────────────────────────────────────────────

  return (
    <div>
      <PageHeader
        title="Reviews"
        description="Manage your reviews and ratings."
      />

      {/* Rating Filters */}
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <span className="text-sm font-medium text-brandslate mr-2">
          Filter by rating:
        </span>
        {RATING_FILTERS.map(({ key, label }) => (
          <Button
            key={key}
            size="sm"
            variant={filterRating === key ? 'solid' : 'bordered'}
            color={filterRating === key ? 'primary' : 'default'}
            onPress={() => handleFilterChange(key)}
            className={
              filterRating === key ? '' : 'border-gray-200 text-brandslate'
            }
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="mt-4">
        {/* Error State */}
        {reviewsError && !isLoadingReviews && (
          <div className="py-16 px-4 text-center">
            <div className="max-w-md mx-auto">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-6">
                <FiXCircle className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-brandslate mb-2">
                Unable to Load Reviews
              </h3>
              <Button
                color="primary"
                variant="shadow"
                onPress={() => refetch()}
              >
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoadingReviews && !reviewsError && reviews.length === 0 && (
          <div className="py-16 px-4 text-center">
            <div className="max-w-md mx-auto">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 mb-6">
                <FiAlertCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-bold text-brandslate mb-2">
                No Reviews Found
              </h3>
              <p className="text-brandslate mb-6">
                {filterRating !== 'all'
                  ? `No reviews with ${filterRating} stars. Try adjusting your filters.`
                  : "You haven't written any reviews yet."}
              </p>
              {filterRating !== 'all' && (
                <Button
                  variant="flat"
                  color="default"
                  onPress={() => handleFilterChange('all')}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Review Cards */}
        {!isLoadingReviews && !reviewsError && reviews.length > 0 && (
          <div className="grid grid-cols-1 gap-4 mt-2">
            {reviews.map((r: any) => {
              const starRating = overallRating(r);
              const canEdit = !!(
                userId &&
                r?.customerId &&
                r.customerId === userId
              );
              const editable = isReviewEditable(
                r?.reviewDate || r?.createdAt || r?.date
              );
              const reviewId = r?.id || r?._id || r?.reviewId;

              return (
                <div
                  key={reviewId}
                  className="border border-gray-200 rounded-2xl p-5 bg-white shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-brandslate text-sm truncate">
                          {r?.title || 'Untitled review'}
                        </h3>
                        {r?.isVerified && (
                          <FiCheckCircle
                            className="w-4 h-4 text-green-500 flex-shrink-0"
                            title="Verified User"
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <StarRating
                          value={starRating}
                          showValue={true}
                          size="sm"
                        />
                        <span className="text-xs text-body-mute">
                          ·{' '}
                          {new Date(r?.reviewDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                        {r?.serviceName && (
                          <>
                            <span className="text-xs text-body-mute">·</span>
                            <button
                              className="text-xs text-primary hover:underline truncate"
                              onClick={() => goToService(r)}
                            >
                              {r?.serviceName}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {canEdit && editable && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs font-mono text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200 whitespace-nowrap">
                          {getTimeRemaining(
                            r?.reviewDate || r?.createdAt || r?.date
                          )}
                        </span>
                        <button
                          onClick={() => {
                            setSelectedReview({ ...r, rating: starRating });
                            setIsEditMode(true);
                            setShowReviewForm(true);
                          }}
                          className="text-xs text-body-mute hover:text-brandslate transition-colors"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-brandslate leading-relaxed mt-3 line-clamp-2">
                    {r?.review || r?.comment || 'No comment provided.'}
                  </p>

                  {/* Images */}
                  {((r?.imageNames?.length || 0) > 0 ||
                    (r?.imageUrls?.length || 0) > 0 ||
                    (r?.images?.length || 0) > 0) && (
                    <div className="mt-3">
                      <ReviewImages
                        imageNames={(r?.imageNames ?? []) as any}
                        imageUrls={
                          ((r?.imageUrls?.length
                            ? r?.imageUrls
                            : (r?.images as any)) ?? []) as any
                        }
                        maxDisplay={6}
                        size="sm"
                        enablePreview
                        layout="grid-4"
                        className=""
                        onImageClick={openImageViewer}
                      />
                    </div>
                  )}

                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleViewReview(r)}
                      className="text-xs text-primary font-medium hover:underline"
                    >
                      View Details
                    </button>
                    {r?.serviceId && (
                      <button
                        onClick={() => goToService(r)}
                        className="text-xs text-body-mute hover:text-brandslate ml-auto"
                      >
                        View Service →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!isLoadingReviews && !reviewsError && totalPages > 0 && (
          <div className="flex justify-end items-end py-5 mt-7">
            <CustomPagination
              page={currentPage}
              initialPage={1}
              total={totalPages}
              size="md"
              onChange={handlePageChange}
            />
          </div>
        )}
      </div>

      {/* ── View Review Modal ─────────────────────────────────────── */}
      {showViewModal && selectedViewReview && (
        <Modal
          isOpen={showViewModal}
          onClose={() => {
            if (!showImageViewer) handleCloseViewModal();
          }}
          size="2xl"
          scrollBehavior="inside"
          isDismissable={!showImageViewer}
          shouldBlockScroll
        >
          <ModalContent>
            <ModalHeader className="p-4 border-b">
              <h2 className="text-xl font-bold text-brandslate">
                {selectedViewReview.title}
              </h2>
            </ModalHeader>
            <ModalBody className="p-4">
              <div className="space-y-4">
                {/* Overall rating */}
                {(() => {
                  const r = overallRating(selectedViewReview);
                  return r > 0 ? (
                    <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium text-brandslate">
                          Overall rating
                        </span>
                        <StarRating value={r} showValue={false} />
                      </div>
                    </div>
                  ) : null;
                })()}

                {/* Review content */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h4 className="font-title text-brandslate mb-2">Review</h4>
                  <p className="text-brandslate leading-relaxed font-bold">
                    {selectedViewReview.comment || 'No comment provided'}
                  </p>
                  {isContentCleaned(selectedViewReview.comment || '') && (
                    <div className="mt-3 flex items-center gap-2">
                      <Chip
                        size="sm"
                        color="warning"
                        variant="flat"
                        startContent={<FiAlertCircle className="w-3 h-3" />}
                      >
                        Content Moderated
                      </Chip>
                      <span className="text-xs text-brandslate">
                        This review was automatically filtered for inappropriate
                        content
                      </span>
                    </div>
                  )}
                </div>

                {/* Detailed ratings in modal */}
                {(selectedViewReview.serviceRating ||
                  selectedViewReview.qualityRating ||
                  selectedViewReview.valueRating ||
                  selectedViewReview.communicationRating ||
                  selectedViewReview.timelinessRating) && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-primary-light to-brandpalecyan px-6 py-4 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary-light rounded-lg flex items-center justify-center">
                          <FiStar className="w-4 h-4 text-primary" />
                        </div>
                        <h4 className="text-lg font-bold text-brandslate">
                          Detailed Ratings
                        </h4>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                          {
                            val: selectedViewReview.serviceRating,
                            label: 'Service Quality',
                            desc: 'Overall service experience',
                            icon: FiCheckCircle,
                            bg: 'bg-primary',
                            border: 'border-primary/20',
                            gradient: 'from-primary-light to-brandpalecyan',
                          },
                          {
                            val: selectedViewReview.qualityRating,
                            label: 'Work Quality',
                            desc: 'Quality of work delivered',
                            icon: FiStar,
                            bg: 'bg-green-500',
                            border: 'border-green-200',
                            gradient: 'from-green-50 to-green-100',
                          },
                          {
                            val: selectedViewReview.valueRating,
                            label: 'Value for Money',
                            desc: 'Worth the investment',
                            icon: FiAlertCircle,
                            bg: 'bg-purple-500',
                            border: 'border-purple-200',
                            gradient: 'from-purple-50 to-purple-100',
                          },
                          {
                            val: selectedViewReview.communicationRating,
                            label: 'Communication',
                            desc: 'Responsiveness & clarity',
                            icon: FiEdit,
                            bg: 'bg-indigo-500',
                            border: 'border-indigo-200',
                            gradient: 'from-indigo-50 to-indigo-100',
                          },
                          {
                            val: selectedViewReview.timelinessRating,
                            label: 'Timeliness',
                            desc: 'On-time delivery',
                            icon: FiAlertCircle,
                            bg: 'bg-orange-500',
                            border: 'border-orange-200',
                            gradient: 'from-orange-50 to-orange-100',
                          },
                        ]
                          .filter(({ val }) => val)
                          .map(
                            ({
                              val,
                              label,
                              desc,
                              icon: Icon,
                              bg,
                              border,
                              gradient,
                            }) => (
                              <div
                                key={label}
                                className={`bg-gradient-to-r ${gradient} rounded-xl p-4 border ${border}`}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-3">
                                    <div
                                      className={`w-10 h-10 ${bg} rounded-lg flex items-center justify-center`}
                                    >
                                      <Icon className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <span className="font-semibold text-brandslate">
                                        {label}
                                      </span>
                                      <p className="text-xs text-brandslate">
                                        {desc}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <StarRating
                                      value={Number(val) || 0}
                                      showValue={false}
                                      size="sm"
                                    />
                                    <span className="text-lg font-bold text-brandslate">
                                      {val}/5
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Images in modal */}
                {((selectedViewReview.imageNames?.length || 0) > 0 ||
                  (selectedViewReview.imageUrls?.length || 0) > 0 ||
                  ((selectedViewReview as any).images?.length || 0) > 0) && (
                  <div className="overflow-hidden">
                    <div className="p-6">
                      <ReviewImages
                        imageNames={
                          (selectedViewReview.imageNames ?? []) as any
                        }
                        imageUrls={
                          (selectedViewReview.imageUrls?.length
                            ? selectedViewReview.imageUrls
                            : (((selectedViewReview as any).images as any) ??
                              [])) as any
                        }
                        maxDisplay={10}
                        size="md"
                        showCount
                        folderName="review-images"
                        layout="grid-4"
                        className=""
                        enablePreview
                        onImageClick={openImageViewer}
                      />
                    </div>
                  </div>
                )}

                {/* Provider response */}
                <div className="bg-white rounded-xl border border-gray-200 p-4 mt-4">
                  <h4 className="font-title text-brandslate mb-2">
                    Provider Response
                  </h4>
                  {selectedViewReview?.providerResponse?.responseText ? (
                    <p className="text-sm text-brandslate">
                      {selectedViewReview.providerResponse.responseText}
                    </p>
                  ) : (
                    <div className="text-sm text-brandslate">
                      No response yet.
                    </div>
                  )}
                </div>
              </div>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {/* ── Review Form Modal ─────────────────────────────────────── */}
      {(showReviewForm || showUrlReviewForm) && (
        <ReviewForm
          key={
            (selectedReview as any)?.id ||
            (selectedReview as any)?._id ||
            (selectedReview as any)?.reviewId ||
            urlBookingData?.bookingId ||
            'new-review'
          }
          isOpen={showReviewForm || showUrlReviewForm}
          onClose={() => {
            setShowReviewForm(false);
            setShowUrlReviewForm(false);
            setSelectedReview(null);
            setIsEditMode(false);
            window.history.replaceState(null, '', window.location.pathname);
          }}
          onSubmit={submitReview}
          providerId={
            isEditMode
              ? 'default-provider'
              : urlBookingData?.providerId || 'default-provider'
          }
          serviceId={
            isEditMode
              ? 'default-service'
              : urlBookingData?.serviceId || 'default-service'
          }
          bookingId={
            isEditMode
              ? 'default-booking'
              : urlBookingData?.bookingId || 'default-booking'
          }
          serviceName={
            isEditMode
              ? 'Service Review'
              : urlBookingData?.serviceName || 'Service Review'
          }
          reviewId={selectedReview?.reviewId}
          isEdit={isEditMode}
        />
      )}

      {/* ── Profanity Warning Modal ───────────────────────────────── */}
      <Modal
        isOpen={showProfanityWarning}
        onClose={handleRejectCleanedReview}
        size="2xl"
        classNames={{
          backdrop:
            'bg-gradient-to-t from-zinc-900 to-zinc-900/10 backdrop-opacity-20',
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <FiAlertCircle className="w-6 h-6 text-orange-500" />
              <span>Content Review Required</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-800 mb-2">
                  Inappropriate Language Detected
                </h4>
                <p className="text-orange-700 text-sm">
                  Our system has detected potentially inappropriate language in
                  your review. We've automatically cleaned the content for you.
                  Please review the changes below:
                </p>
              </div>
              {profanityWarningData && (
                <div className="space-y-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h5 className="font-medium text-red-800 mb-2">
                      Issues Found:
                    </h5>
                    <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                      {profanityWarningData.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <h5 className="font-medium text-brandslate">
                        Original Content:
                      </h5>
                      <div className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                        <div className="mb-2">
                          <strong>Title:</strong>{' '}
                          {profanityWarningData.originalData.title}
                        </div>
                        <div>
                          <strong>Review:</strong>{' '}
                          {profanityWarningData.originalData.comment}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h5 className="font-medium text-brandslate">
                        Cleaned Content:
                      </h5>
                      <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                        <div className="mb-2">
                          <strong>Title:</strong>{' '}
                          {profanityWarningData.cleanedData?.title}
                        </div>
                        <div>
                          <strong>Review:</strong>{' '}
                          {profanityWarningData.cleanedData?.review}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="light"
              onPress={handleRejectCleanedReview}
            >
              Cancel Submission
            </Button>
            <Button
              color="success"
              onPress={handleAcceptCleanedReview}
              startContent={<FiCheckCircle />}
            >
              Accept Cleaned Version
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Image Viewer ──────────────────────────────────────────── */}
      {showImageViewer && viewerImages.length > 0 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[9999]"
          onClick={closeImageViewer}
        >
          <div className="relative w-full h-full flex items-center justify-center p-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeImageViewer();
              }}
              className="absolute top-6 right-6 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-3 transition-all z-[10000] backdrop-blur-sm border border-white border-opacity-20"
              title="Close (ESC)"
            >
              <FiXCircle className="w-7 h-7" />
            </button>

            {viewerImages.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToPreviousImage();
                }}
                className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-4 transition-all z-[10000] backdrop-blur-sm border border-white border-opacity-20"
                title="Previous (←)"
              >
                <FiChevronLeft className="w-8 h-8" />
              </button>
            )}

            <div
              className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {imageLoading && (
                <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center rounded-xl z-10">
                  <div className="text-white text-lg flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                    Loading...
                  </div>
                </div>
              )}
              <img
                src={viewerImages[currentImageIndex]}
                alt={`Review ${currentImageIndex + 1}`}
                className="max-w-full max-h-full object-contain rounded-xl shadow-2xl border border-white border-opacity-10 transition-opacity duration-300"
                onLoad={() => setImageLoading(false)}
                onLoadStart={() => setImageLoading(true)}
                onError={(e) => {
                  setImageLoading(false);
                  (e.target as HTMLImageElement).src =
                    'https://via.placeholder.com/600x400?text=Image+Not+Found&bg=1a1a1a&color=ffffff';
                }}
                style={{
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                  minWidth: '300px',
                  minHeight: '200px',
                  opacity: imageLoading ? 0.5 : 1,
                }}
              />
            </div>

            {viewerImages.length > 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  goToNextImage();
                }}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-4 transition-all z-[10000] backdrop-blur-sm border border-white border-opacity-20"
                title="Next (→)"
              >
                <FiChevronRight className="w-8 h-8" />
              </button>
            )}

            {viewerImages.length > 1 && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-6 py-3 rounded-full text-base font-medium backdrop-blur-sm border border-white border-opacity-20">
                {currentImageIndex + 1} of {viewerImages.length}
              </div>
            )}
            <div className="absolute top-6 left-6 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm backdrop-blur-sm border border-white border-opacity-20">
              Review Image {currentImageIndex + 1}
            </div>
            <div className="absolute bottom-6 right-6 bg-black bg-opacity-70 text-white px-4 py-2 rounded-lg text-sm backdrop-blur-sm border border-white border-opacity-20">
              <div className="flex items-center gap-2">
                <span>←→</span>
                <span>Navigate</span>
                <span>•</span>
                <span>ESC</span>
                <span>Close</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
